import React, { useState } from "react";

import { Grid } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Button, { Color } from "../../../components/Form/Button";
import TextField from "../../../components/Form/Input";
import SkeletonComponent from "../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../context/StateContext";
import useOnBoardingContext from "../../../hooks/useOnBoardingContext";
import useYupValidation from "../../../hooks/useYupValidation";
import { Branch } from "../../../models/branch";
import { Error } from "../../../models/error";
import {
  DetailsFieldTitle,
  Separator,
} from "../../../pages/private/Shared/Settings/Company/FormCompany/styles";
import BranchService from "../../../services/branch.service";
import BranchesModal from "../BranchesModal";
import {
  AddBranchButton,
  BranchText,
  RegisteredBranchesBadge,
  RegisteredBranchesContainer,
  RegisteredBranchesCounter,
  RegisteredBranchesText,
} from "./styles";

interface BranchesFieldProps {
  loading: boolean;
}

export const BranchesField = ({ loading }: BranchesFieldProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { setTotalBranches } = useOnBoardingContext();

  const [branch, setBranch] = useState<Branch | undefined>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [openBranchesModal, setOpenBranchesModal] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { refetch: refetchBranchTotal } = useQuery(
    ["onboarding.branch.total"],
    () =>
      BranchService.count({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setTotal(response.data);
          setTotalBranches?.(response.data);
        }
      },
    }
  );

  const { mutate: createBranch, isLoading: isCreating } = useMutation(
    ["branch.create"],
    (branch: Branch) => BranchService.create(branch),
    {
      onSuccess: () => {
        refetchBranchTotal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const isMinimalRequiredDataFilled = (branch?: Branch) => {
    if (!branch) return false;

    const branchName = branch?.name?.trim();
    const branchCode = branch?.code?.trim();
    const branchDescription = branch?.description?.trim();

    if (branchName && branchCode && branchDescription) return true;

    return false;
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const handleValidateSuccess = (branch?: Branch): boolean => {
    schema.validateSync(
      {
        code: branch?.code,
        name: branch?.name,
        description: branch?.description,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: yup.ValidationError): boolean => {
    if (error) {
      setErrors(error.inner);
    }

    return false;
  };

  const handleValidate = (branch?: Branch): boolean => {
    try {
      return handleValidateSuccess(branch);
    } catch (error) {
      if (error instanceof yup.ValidationError) return handleValidateError(error);

      return false;
    }
  };

  const handleCreateBranch = () => {
    if (handleValidate(branch)) {
      if (branch && currentUser) {
        const newBranch = {
          ...branch,
          companyId: currentUser?.companyId,
        } as Branch;

        createBranch(newBranch, {
          onSuccess: () => {
            NotificationToast.success(translate("branchAddedSuccessfully"));
            setBranch(undefined);
          },
        });
      }
    }
  };

  return (
    <>
      <Grid container direction="row">
        <Separator />
      </Grid>
      <Grid container item direction="row" alignItems="center">
        <DetailsFieldTitle>{translate("branch")}</DetailsFieldTitle>
        {total > 0 && (
          <RegisteredBranchesContainer>
            <RegisteredBranchesText>{translate("registered")}</RegisteredBranchesText>
            <RegisteredBranchesBadge>
              <RegisteredBranchesCounter>{total}</RegisteredBranchesCounter>
            </RegisteredBranchesBadge>
          </RegisteredBranchesContainer>
        )}
      </Grid>
      <Grid container item direction="row">
        <BranchText>
          {translate("inShipeeziYouCanManageBranchesPhysicalLocationsOfYourBusinessAMinimumOf")}{" "}
          <strong>{translate("oneBranch")}</strong> {translate("mustBeRecorded")}
        </BranchText>
      </Grid>

      <Grid item xs={12} sm={6}>
        <SkeletonComponent isLoading={loading}>
          <TextField
            name="branchName"
            data-cy="branchName"
            placeholder={translate("name")}
            label={translate("branchName")}
            value={branch?.name || ""}
            required
            error={fieldHasError("name")}
            errorMessage={getErrorByField("name")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBranch((state) =>
                state
                  ? { ...state, name: event?.target.value }
                  : ({ name: event?.target.value } as Branch)
              );
            }}
          />
        </SkeletonComponent>
      </Grid>
      <Grid item xs={12} sm={6}>
        <SkeletonComponent isLoading={loading}>
          <TextField
            name="branchCode"
            data-cy="branchCode"
            placeholder={translate("code")}
            label={translate("code")}
            value={branch?.code || ""}
            required
            error={fieldHasError("code")}
            errorMessage={getErrorByField("code")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBranch((state) =>
                state
                  ? { ...state, code: event?.target.value }
                  : ({ code: event?.target.value } as Branch)
              );
            }}
          />
        </SkeletonComponent>
      </Grid>

      <Grid item xs={12}>
        <SkeletonComponent isLoading={loading}>
          <TextField
            name="branchDescription"
            data-cy="branchDescription"
            placeholder={translate("description")}
            label={translate("description")}
            value={branch?.description || ""}
            required
            error={fieldHasError("description")}
            errorMessage={getErrorByField("description")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBranch((state) =>
                state
                  ? { ...state, description: event?.target.value }
                  : ({ description: event?.target.value } as Branch)
              );
            }}
          />
        </SkeletonComponent>
      </Grid>

      <Grid container item xs={12} justifyContent="flex-end">
        {total > 0 && (
          <Button customColor={Color.DEFAULT} onClick={() => setOpenBranchesModal(true)}>
            {translate("viewBranches")}
          </Button>
        )}
        <AddBranchButton
          disabled={!isMinimalRequiredDataFilled(branch) || isCreating}
          loading={isCreating}
          customColor={Color.DEFAULT}
          onClick={handleCreateBranch}
        >
          {translate("addBranch")}
        </AddBranchButton>
      </Grid>
      {openBranchesModal && (
        <BranchesModal
          open={openBranchesModal}
          total={total}
          closeModal={() => setOpenBranchesModal(false)}
        />
      )}
    </>
  );
};
